import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  fetchGuardianProfile,
  hasPermission,
  GuardianPermission,
  GuardianProfile,
} from './guardianService';

export interface GuardianDiagnostics {
  /** Role names Guardian actually returned for this user. */
  rolesReturned: string[];
  /** Raw permission strings Guardian returned, for support. */
  permissionsReturned: string[];
  /** Role names / permission the backend was looking for. */
  expectedRoles: string[];
  expectedPermission: string;
  /** What a denied user should request in order to gain access. */
  accessRequestHint: string;
}

interface GuardianContextValue {
  roles: string[];
  permissions: GuardianPermission[];
  isLoading: boolean;
  isAuthorized: boolean;
  error: string | null;
  diagnostics: GuardianDiagnostics | null;
  hasRole: (role: string) => boolean;
  hasPermission: (name: string, scope?: string) => boolean;
  refresh: () => void;
}

const defaultValue: GuardianContextValue = {
  roles: [],
  permissions: [],
  isLoading: true,
  isAuthorized: false,
  error: null,
  diagnostics: null,
  hasRole: () => false,
  hasPermission: () => false,
  refresh: () => undefined,
};

const GuardianContext = createContext<GuardianContextValue>(defaultValue);

export const useGuardian = (): GuardianContextValue => useContext(GuardianContext);

function toDiagnostics(profile: GuardianProfile): GuardianDiagnostics {
  return {
    rolesReturned: profile.roles ?? [],
    permissionsReturned: profile.rawPermissions ?? [],
    expectedRoles: profile.expected?.allowedRoles ?? [],
    expectedPermission: profile.expected?.requiredPermission ?? '',
    accessRequestHint: profile.accessRequestHint ?? '',
  };
}

const AUTH_REAUTH_ERROR_PATTERN = /invalid or expired token|no sentinel access token available|authorization header with bearer token is required|authentication is required/i;

function shouldReauthenticate(message: string): boolean {
  return AUTH_REAUTH_ERROR_PATTERN.test(message);
}

interface GuardianProviderProps {
  children: React.ReactNode;
}

/**
 * Loads the caller's Guardian authorization once Sentinel authentication has
 * completed, and exposes it to the app.
 *
 * Sentinel establishes identity; Guardian decides access. The decision itself
 * is made by the backend — this provider surfaces it, and carries enough
 * diagnostic detail that a denial can be told apart from a misconfiguration.
 */
export const GuardianProvider: React.FC<GuardianProviderProps> = ({ children }) => {
  const auth = useAuth();
  const [roles, setRoles] = useState<string[]>([]);
  const [permissions, setPermissions] = useState<GuardianPermission[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [diagnostics, setDiagnostics] = useState<GuardianDiagnostics | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const reauthInProgressRef = useRef(false);

  const refresh = useCallback(() => setReloadToken((value) => value + 1), []);

  const isAuthenticated = auth.isAuthenticated;
  const subject = auth.user?.profile?.sub;

  useEffect(() => {
    // Sentinel has to finish first: without a token there is nothing to
    // authorize, and the backend derives linkUuid from that token's sub.
    if (!isAuthenticated || !subject) {
      if (!reauthInProgressRef.current) {
        setIsLoading(false);
      }
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      let keepLoading = false;
      setIsLoading(true);
      setError(null);

      try {
        // Use the exact current token held by the OIDC provider. Reading a
        // separately reconstructed session-storage value can forward a stale
        // token after silent renewal in a deployed SPA.
        const profile = await fetchGuardianProfile(auth.user?.access_token);
        if (cancelled) return;

        reauthInProgressRef.current = false;
        setRoles(profile.roles ?? []);
        setPermissions(profile.permissions ?? []);
        setIsAuthorized(profile.isAuthorized);
        setDiagnostics(toDiagnostics(profile));
        setError(
          profile.isAuthorized
            ? null
            : 'Guardian did not return a role that grants access to this application.',
        );
      } catch (err) {
        if (cancelled) return;

        const message = err instanceof Error ? err.message : 'Failed to load Guardian profile';

        setRoles([]);
        setPermissions([]);
        setIsAuthorized(false);
        setDiagnostics(null);
        setError(message);
      } finally {
        if (!cancelled && !keepLoading) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [auth.user?.access_token, isAuthenticated, subject, reloadToken]);

  const hasRole = useCallback((role: string) => roles.includes(role), [roles]);

  const checkPermission = useCallback(
    (name: string, scope?: string) => hasPermission(permissions, { name, scope }),
    [permissions],
  );

  const contextValue = useMemo(
    () => ({
      roles,
      permissions,
      isLoading,
      isAuthorized,
      error,
      diagnostics,
      hasRole,
      hasPermission: checkPermission,
      refresh,
    }),
    [roles, permissions, isLoading, isAuthorized, error, diagnostics, hasRole, checkPermission, refresh],
  );

  return (
    <GuardianContext.Provider value={contextValue}>
      {children}
    </GuardianContext.Provider>
  );
};
